import { useContext } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Result } from "bloggers-backend/lib/domain/Result";
import { SettingsSnapshot, UpdateSettingsRequest } from "bloggers-backend/lib/settings/domain";
import { AppContext } from "../../../AppContext";

const settingsQueryKey = ["settings"];

type SettingsEvent = "settings.getAll" | "settings.update" | "settings.validateOnUpdate";

interface SettingsCall<Data> {
  event: SettingsEvent;
  data: Data;
}

export function useSettings() {
  const {client} = useContext(AppContext);

  return useQuery(settingsQueryKey, async () => {
    const result = await client<SettingsCall<{}>, Result<SettingsSnapshot>>({event: "settings.getAll", data: {}});
    if (!result.success) {
      throw new Error(result.reason);
    }
    return result.value;
  });
}

export function useUpdateSettings() {
  const {client} = useContext(AppContext);
  const queryClient = useQueryClient();

  return useMutation(
    (request: UpdateSettingsRequest) =>
      client<SettingsCall<UpdateSettingsRequest>, Result<Record<string, unknown>>>({event: "settings.update", data: request}),
    {
      onSuccess: async (result) => {
        if (result.success) {
          await queryClient.invalidateQueries(settingsQueryKey);
        }
      },
    },
  );
}

export function useValidateSettings() {
  const {client} = useContext(AppContext);

  return (request: UpdateSettingsRequest) =>
    client<SettingsCall<UpdateSettingsRequest>, Result<Record<string, unknown>>>({event: "settings.validateOnUpdate", data: request});
}
